import cn from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ColumnVisibility } from '@/components/common/ColumnVisibility/ColumnVisibility';
import { useColumnVisibility } from '@/components/common/ColumnVisibility/useColumnVisibility';
import { advanceFiltersNormalizer } from '@/helpers/advanceFilters/advanceFiltersNormalizer.helper.ts';
import {
  filterCampaignPlansByAdvertiser,
  filterInsertionOrdersByCampaignPlans,
  reconcileCascadingAdvanceFilters
} from '@/helpers/campaignFilters/cascadingFilters.helper';
import { useModals } from '@/router';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import EntityTabEmptyState from '../EntityTabEmptyState/EntityTabEmptyState';
import { LineItemsList } from '../LineItemsList';
import {
  LINE_ITEMS_COLUMN_VISIBILITY_KEY,
  LINE_ITEMS_HIDEABLE_COLUMN_IDS,
  useLineItemsColumnLabels
} from '../LineItemsList/lineItemsColumns';
import { ListFilters } from '../ListFilters';
import { Advertiser } from '../ListFilters/Advertiser';
import { CampaignPlan } from '../ListFilters/CampaignPlan';
import { DateRange } from '../ListFilters/DateRange';
import InsertionOrder from '../ListFilters/InsertionOrder/InsertionOrder.tsx';
import { SearchByFilters } from '../ListFilters/SearchByFilters';
import { LINE_ITEM_FILTER_GROUPS } from '../ListFilters/SearchByFilters/SearchByFilters.tsx';
import { Status } from '../ListFilters/Status';
import { getColumnVisibilityOptions } from '../Shared/columnVisibilityOptions';
import MultiLinesSelectionMenu from './MultiLinesSelectionMenu.tsx';

import type { SearchByFilterEntry } from '../ListFilters/SearchByFilters';

import './lineItemTab.scss';

export default function LineItemsTab() {
  const { applyVisibleColumns, isVisible: isColumnVisible } = useColumnVisibility(
    LINE_ITEMS_COLUMN_VISIBILITY_KEY,
    LINE_ITEMS_HIDEABLE_COLUMN_IDS
  );
  const columnLabels = useLineItemsColumnLabels();
  const {
    advertisers,
    cacheSeed,
    campaigns,
    hasLineItems,
    insertionOrders,
    isLoadingCountLineItems,
    isLoadingGetAdvertisers,
    isLoadingGetCampaigns,
    isLoadingGetInsertionOrders,
    lineItemsListFilters,
    setCacheSeed,
    setLineItemsListFilters,
    setSelectedLineItems,
    setShowSearchByFilters,
    showSearchByFilters
  } = useCampaignStore() as CampaignsStore;

  const isLoading =
    isLoadingCountLineItems || isLoadingGetAdvertisers || isLoadingGetCampaigns || isLoadingGetInsertionOrders;

  const { formatMessage } = useIntl();
  const modals = useModals();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const ioId = searchParams.get('ioId');
  const cpId = searchParams.get('cpId');
  const advertiserIdParam = searchParams.get('advertiserId');
  const [drillDownResyncKey, setDrillDownResyncKey] = useState(0);
  const [selectionResetToken, setSelectionResetToken] = useState(0);
  const hasAppliedDrillDown = useRef(false);

  const internalCacheSeed = cacheSeed + JSON.stringify(lineItemsListFilters);
  const statusFilter = lineItemsListFilters.advanceFilters?.find((el: Record<string, any>) => el.id === 'status');
  const datesFilter = lineItemsListFilters.advanceFilters?.find((el: Record<string, any>) => el.id === 'dates');
  const campaignPlansFilter = lineItemsListFilters.advanceFilters?.find((el: Record<string, any>) => {
    return el.id === 'campaignPlans';
  });
  const insertionOrdersFilter = lineItemsListFilters.advanceFilters?.find((el: Record<string, any>) => {
    return el.id === 'insertionOrders';
  });
  const advertiserFilter = lineItemsListFilters.advanceFilters?.find((el: Record<string, any>) => {
    return el.id === 'advertiserIds';
  });
  const filteredCampaigns = filterCampaignPlansByAdvertiser(campaigns, advertiserFilter?.value);
  const effectiveCampaignPlanIds = campaignPlansFilter?.value?.length
    ? campaignPlansFilter.value
    : filteredCampaigns?.map(({ _id }) => _id);
  const filteredInsertionOrders = filterInsertionOrdersByCampaignPlans(insertionOrders, effectiveCampaignPlanIds);

  useEffect(() => {
    if (hasAppliedDrillDown.current || (!ioId && !cpId && !advertiserIdParam)) {
      return;
    }
    hasAppliedDrillDown.current = true;
    setLineItemsListFilters({
      advanceFilters: [
        ...(ioId ? [{ id: 'insertionOrders', value: [ioId] }] : []),
        ...(cpId ? [{ id: 'campaignPlans', value: [cpId] }] : []),
        ...(advertiserIdParam ? [{ id: 'advertiserIds', value: [advertiserIdParam] }] : [])
      ]
    });
    setDrillDownResyncKey(prevKey => prevKey + 1);
    setSearchParams(
      prevParams => {
        prevParams.delete('ioId');
        prevParams.delete('cpId');
        prevParams.delete('advertiserId');
        return prevParams;
      },
      { replace: true }
    );
  }, [ioId, cpId, advertiserIdParam]);

  const getFiltersByOrigin = (origin: 'advance' | 'searchBy'): SearchByFilterEntry[] => {
    const advanceFilters = lineItemsListFilters.advanceFilters || [];
    if (origin === 'advance') {
      return advanceFilters.filter((f: Record<string, any>) => !LINE_ITEM_FILTER_GROUPS.includes(f.id));
    }
    return advanceFilters.filter((f: Record<string, any>) => LINE_ITEM_FILTER_GROUPS.includes(f.id));
  };

  const handleFilter = (newFilters: Record<string, any>, isSearchByFiltersChange = false) => {
    if (!isSearchByFiltersChange) {
      setLineItemsListFilters({
        ...newFilters,
        advanceFilters: reconcileCascadingAdvanceFilters(
          [...newFilters.advanceFilters, ...getFiltersByOrigin('searchBy')],
          campaigns,
          insertionOrders
        )
      });
    } else {
      setLineItemsListFilters({
        ...lineItemsListFilters,
        advanceFilters: reconcileCascadingAdvanceFilters(
          [...getFiltersByOrigin('advance'), ...newFilters.advanceFilters],
          campaigns,
          insertionOrders
        )
      });
    }
    setCacheSeed();
  };

  const handleSearchByFiltersChange = (newFilters: SearchByFilterEntry[]) => {
    handleFilter({ ...lineItemsListFilters, advanceFilters: newFilters }, true);
  };

  const handleReset = () => {
    setLineItemsListFilters({});
    setCacheSeed();
  };

  const handleListChange = () => {
    setCacheSeed();
  };

  const leftFilters = [
    {
      className: 'campaignsListsFilters__leftFiltersSelectorSmall',
      component: (
        <Advertiser advertisers={advertisers} id="lineItemTabAdvertiserFilter" value={advertiserFilter?.value} />
      ),
      id: 'lineItemsFilters__advertiserFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersSelectorSmall',
      component: (
        <CampaignPlan campaigns={filteredCampaigns} id="lineItemTabCPFilter" value={campaignPlansFilter?.value} />
      ),
      id: 'lineItemsFilters__campaignPlansFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersInsertionOrderSelector',
      component: (
        <InsertionOrder
          id="lineItemTabIOFilter"
          insertionOrders={filteredInsertionOrders}
          value={insertionOrdersFilter?.value}
        />
      ),
      id: 'lineItemsFilters__insertionOrdersFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersStatus',
      component: <Status value={statusFilter?.value} />,
      id: 'lineItemsFilters__statusFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersDateRange',
      component: <DateRange id="tab_insertionOrder_date_filter" value={datesFilter?.value} />,
      id: 'lineItemsFilters__dateRangeFilter',
      sendCallback: true
    }
  ];

  const normalizedFilters = useMemo(() => {
    let filters: Record<string, any> = {};
    if (lineItemsListFilters.text) {
      filters.name = encodeURIComponent(lineItemsListFilters.text);
    }
    filters = advanceFiltersNormalizer(lineItemsListFilters.advanceFilters, filters);
    return filters;
  }, [lineItemsListFilters.text, lineItemsListFilters.advanceFilters]);

  if (!hasLineItems && !isLoading) {
    return (
      <EntityTabEmptyState
        buttonProps={{
          onClick: () =>
            modals.open('/campaigns/new/lineItemModal', {
              at: { pathname: '/campaigns', search: location.search },
              state: { modalId: 'lineItem-modal' }
            }),
          text: formatMessage({
            defaultMessage: 'Create Line Item',
            id: 'lineItems.emptyState.createButton'
          })
        }}
        description={formatMessage({
          defaultMessage: 'Create your first Line Item',
          id: 'lineItems.emptyState.description'
        })}
        title={formatMessage({
          defaultMessage: 'Get Started Today!',
          id: 'lineItems.emptyState.title'
        })}
      />
    );
  }

  return (
    <div className={cn('lineItemsTabContent', { 'lineItemsTabContent--withFilters': showSearchByFilters })}>
      <div className="lineItemsTabContent__main">
        <div className="lineItemsTabContent__filtersAndMenu">
          <ListFilters
            extraActions={
              <ColumnVisibility
                columns={getColumnVisibilityOptions(columnLabels, LINE_ITEMS_HIDEABLE_COLUMN_IDS, isColumnVisible)}
                onApply={applyVisibleColumns}
                prefixId="lineItemTab"
              />
            }
            forceResyncKey={drillDownResyncKey}
            initialFilters={lineItemsListFilters}
            leftFilters={leftFilters}
            onFilter={handleFilter}
            onReset={handleReset}
            onShowFiltersClick={setShowSearchByFilters}
            prefixId="lineItemTab"
            searchByFilters={getFiltersByOrigin('searchBy')}
          />
          <MultiLinesSelectionMenu onBulkActionComplete={() => setSelectionResetToken(token => token + 1)} />
        </div>
        <LineItemsList
          cacheSeed={internalCacheSeed}
          filters={normalizedFilters}
          isColumnVisible={isColumnVisible}
          // onClearFilters={handleReset} TODO: use this when the empty state in the list supports an action
          onListChange={handleListChange}
          onSelectionChange={setSelectedLineItems}
          selectionResetToken={selectionResetToken}
        />
      </div>
      {showSearchByFilters ? (
        <div className="lineItemsTabContent__filters">
          <SearchByFilters
            onClose={() => setShowSearchByFilters(false)}
            onFiltersChange={handleSearchByFiltersChange}
            value={getFiltersByOrigin('searchBy')}
            variant="lineItem"
          />
        </div>
      ) : null}
    </div>
  );
}
